import axios, { redirectToErrorPage, redirectToSessionExpiredPage } from './axios-config';
import { addAuthorizationHeader, addTrackingHeader, buildApiEndpoint, getRequestBody } from '../helper-fns/apiHelper';
import {
	IAmendOrderRequest,
	IAmendOrderResponse,
	IApiErrorResponse,
	IBookingInfoRequest,
	ICancelInfo,
	ICartOrderResponse,
	ICompleteCartOrder,
	ICreateUuidResponse,
	IGetPassageResponse,
	IOrderAmendRequest,
	IOrderInfoRequest,
	IOrderInfoResponse,
	IOrderListRequest,
	IOrderListResponse,
	IOrderRequest,
	IOrderResponse,
	Locales,
} from '../models';

const handleApiException = ({ response }: IApiErrorResponse, lang: Locales | undefined = 'de'): ICreateUuidResponse => {
	const {
		status: code,
		data: { errors },
	} = response;
	if (code === 401) {
		redirectToSessionExpiredPage(lang);
	}
	redirectToErrorPage(errors, lang);
	return { errors, code };
};

// workaround
const handleOrderApiException = (
	{ response }: IApiErrorResponse,
	lang: Locales | undefined = 'de',
): ICreateUuidResponse => {
	const {
		status: code,
		data: { errors },
	} = response;
	if (code === 400) {
		redirectToErrorPage(errors, lang);
	}
	if (code === 401) {
		redirectToSessionExpiredPage(lang);
	}
	redirectToSessionExpiredPage(lang, true);
	return { errors, code };
};

export const OrderService = {
	completeOrder: async (
		body: IOrderRequest,
		token: string | null,
		brokerNo?: string | null,
	): Promise<IOrderResponse> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint(`/offer/book`), getRequestBody({ body, brokerNo }), addTrackingHeader(config))
			.then(({ data }) => data)
			.catch((res: IApiErrorResponse) => {
				handleApiException(res);
			});
	},
	completeCartOrder: async ({ body, token, brokerNo, lang }: ICompleteCartOrder): Promise<ICartOrderResponse> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint(`/cart/book`), getRequestBody({ body, brokerNo, lang }), addTrackingHeader(config))
			.then(({ data }) => data)
			.catch((res: IApiErrorResponse) => {
				handleOrderApiException(res, lang);
			});
	},
	cancelOrder: async (
		body: { orderDetailUuid: string },
		token: string,
		lang?: Locales,
	): Promise<IOrderInfoResponse> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint(`/order/cancel`), getRequestBody({ body, lang }), addTrackingHeader(config))
			.then(({ data }) => data)
			.catch((res: IApiErrorResponse) => handleApiException(res));
	},
	createEmailUuid: async (body: ICancelInfo, lang?: Locales): Promise<ICreateUuidResponse> => {
		return await axios
			.post(buildApiEndpoint(`/order/createEmailUuid`), getRequestBody({ body, lang }), addTrackingHeader())
			.then(({ data }) => data)
			.catch((res: IApiErrorResponse) => handleApiException(res, lang));
	},
	getOrderInfo: async (body: IOrderInfoRequest, lang?: Locales): Promise<IOrderInfoResponse> =>
		await axios
			.post(buildApiEndpoint(`/order/getInfo`), getRequestBody({ body, lang }), addTrackingHeader())
			.then(({ data }) => data)
			.catch((res: IApiErrorResponse) => handleApiException(res, lang)),
	getOrderList: async (body: IOrderListRequest, lang?: Locales): Promise<IOrderListResponse> =>
		await axios
			.post(buildApiEndpoint(`/order/getList`), getRequestBody({ body, lang }), addTrackingHeader())
			.then(({ data }) => data)
			.catch((res: IApiErrorResponse) => handleApiException(res, lang)),
	amendOrderPayment: async (
		body: IAmendOrderRequest,
		token: string | null,
		lang?: Locales,
	): Promise<IAmendOrderResponse> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint(`/order/amendPayment`), getRequestBody({ body, lang }), addTrackingHeader(config))
			.then(({ data }) => data)
			.catch((res: IApiErrorResponse) => handleApiException(res, lang));
	},
	updateOrderInfo: async (body: IOrderAmendRequest, token: string, lang?: Locales): Promise<IOrderInfoResponse> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint(`/order/amend`), getRequestBody({ body, lang }), addTrackingHeader(config))
			.then(({ data }) => data)
			.catch((res: IApiErrorResponse) => handleApiException(res, lang));
	},
	getPassage: async (
		body: { orderDetailUuid: string },
		token: string,
		lang?: Locales,
	): Promise<IGetPassageResponse> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint('/flex/getPassage'), getRequestBody({ body, lang }), config)
			.then(({ data }) => data)
			.catch((res: IApiErrorResponse) => handleApiException(res, lang));
	},
	getBookingInfo: async (body: IBookingInfoRequest, lang?: Locales): Promise<ICartOrderResponse> => {
		const config = addAuthorizationHeader(body.token);

		return await axios
			.post(buildApiEndpoint(`/order/getBookingInfo`), getRequestBody({ body, lang }), addTrackingHeader(config))
			.then(({ data }) => data)
			.catch(() => {});
	},
};
